import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getPracticeInfo, captureVisit, PracticeInfo, Slot } from './api/bookingApi';
import StepIndicator from './components/StepIndicator';
import StatusScreen from './components/StatusScreen';
import PhoneStep from './components/PhoneStep';
import ExistingPatientStep from './components/ExistingPatientStep';
import NewPatientStep from './components/NewPatientStep';
import SlotPickerStep from './components/SlotPickerStep';
import ConfirmationStep from './components/ConfirmationStep';
import ApptTypeStep from './components/ApptTypeStep';
import Shell from './components/Shell';
import LoadingState from './components/LoadingState';

// Phone → verify (2a) or register (2b) → appointment type (2c) → slot (3) → confirmation (4).
// Confirmation is terminal: forms are no longer collected in this flow (operaitor/backend#1604
// slice 1) — when a visit needs them, appointment-prep texts the patient a link.
type Step = 1 | '2a' | '2b' | '2c' | 3 | 4;

const EXTERNAL_BOOKING_THANK_YOU_URLS: Record<string, string> = {
  bleudentist: 'https://bleudentist.com/thank-you/',
};

const EXTERNAL_REDIRECT_EXCLUDED_PARAMS = new Set(['prefill', 'token']);

function buildExternalThankYouUrl(baseUrl: string): string {
  const url = new URL(baseUrl);
  const currentParams = new URLSearchParams(window.location.search);
  currentParams.forEach((value, key) => {
    if (!EXTERNAL_REDIRECT_EXCLUDED_PARAMS.has(key.toLowerCase())) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

export default function App() {
  const { slug } = useParams<{ slug?: string }>();
  const clientId = slug || null;

  const [step, setStep] = useState<Step>(1);
  const [practiceInfo, setPracticeInfo] = useState<PracticeInfo | null>(null);
  const [practiceError, setPracticeError] = useState('');
  const [practiceLoading, setPracticeLoading] = useState(false);

  // Patient state
  const [phone, setPhone] = useState('');
  const [isExistingPatient, setIsExistingPatient] = useState(false);
  const [patientToken, setPatientToken] = useState('');
  const [patientName, setPatientName] = useState('');
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');

  // Appointment type selection
  const [apptType, setApptType] = useState('');

  // Booking result
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [bookedApptType, setBookedApptType] = useState('');

  useEffect(() => {
    if (!clientId) return;
    setPracticeLoading(true);
    getPracticeInfo(clientId)
      .then((data) => setPracticeInfo(data))
      .catch((err: Error) => setPracticeError(err.message || 'Could not load practice information.'))
      .finally(() => setPracticeLoading(false));
  }, [clientId]);

  // Campaign attribution: record this landing (URL tracking params + referrer)
  // once per session. Best-effort — never blocks the booking flow.
  useEffect(() => {
    if (!clientId) return;
    captureVisit(clientId);
  }, [clientId]);

  function handleIdentified({ phone: p, exists }: { phone: string; exists: boolean }) {
    setPhone(p);
    setIsExistingPatient(exists);
    setStep(exists ? '2a' : '2b');
  }

  function handleVerified({ patientToken: token, patientName: name }: { patientToken: string; patientName?: string }) {
    setPatientToken(token);
    if (name) setPatientName(name);
    setStep('2c');
  }

  function handleRegistered({ patientToken: token, patientName: name }: { patientToken: string; patientName: string }) {
    setPatientToken(token);
    setPatientName(name);
    setStep('2c');
  }

  function handleApptTypeSelected(type: string) {
    setApptType(type);
    setStep(3);
  }

  function handleBooked({ slot, apptType }: { slot: Slot; apptType: string; confirmation: unknown }) {
    const externalThankYouUrl = clientId ? EXTERNAL_BOOKING_THANK_YOU_URLS[clientId.toLowerCase()] : undefined;
    if (externalThankYouUrl) {
      window.location.assign(buildExternalThankYouUrl(externalThankYouUrl));
      return;
    }

    setSelectedSlot(slot);
    setBookedApptType(apptType);
    setStep(4);
  }

  function handleReset() {
    setStep(1);
    setPhone('');
    setIsExistingPatient(false);
    setPatientToken('');
    setPatientName('');
    setApptType('');
    setSelectedSlot(null);
    setBookedApptType('');
    setSessionExpiredMsg('');
  }

  function handleSessionExpired() {
    // The patient_token lives 30 minutes, so anyone slow on the slot picker lands here.
    setStep(1);
    setPatientToken('');
    setSessionExpiredMsg('Your session expired. Please verify your identity again.');
  }

  // No client ID
  if (!clientId) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-gray-500">No booking link provided. Please use the link shared by your practice.</p>
        </div>
      </Shell>
    );
  }

  // Loading practice info
  if (practiceLoading) {
    return (
      <Shell>
        <LoadingState message="Loading practice..." />
      </Shell>
    );
  }

  if (practiceError) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-red-600 font-medium">{practiceError}</p>
          <p className="text-gray-500 text-sm mt-2">
            Please check the link and try again.
          </p>
        </div>
      </Shell>
    );
  }

  // A non-ready bookable-service catalog must never reach phone entry: identifying as a
  // new patient registers a real PMS chart before the menu reveals there is nothing to
  // book (backend#2009). Absence of the flag fails open (older backends don't send it);
  // a present-but-empty menu is the interim signal until the field ships.
  if (
    practiceInfo &&
    (practiceInfo.automated_booking_available === false ||
      (Array.isArray(practiceInfo.appointment_types) && practiceInfo.appointment_types.length === 0))
  ) {
    return (
      <Shell practiceInfo={practiceInfo}>
        <StatusScreen
          type="call_office"
          heading="Online booking is temporarily unavailable"
          message={
            practiceInfo.booking_unavailable_message ??
            'Please call the office to schedule your appointment.'
          }
          phoneNumber={practiceInfo.phone_number ?? undefined}
        />
      </Shell>
    );
  }

  return (
    <Shell practiceInfo={practiceInfo}>
      {step !== 4 && <StepIndicator step={step} />}

      <div className={step === 4 ? '' : 'relative'}>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}

            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {step === 1 && (
              <>
                {sessionExpiredMsg && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    {sessionExpiredMsg}
                  </div>
                )}
                <PhoneStep
                  clientId={clientId}
                  practiceInfo={practiceInfo}
                  onIdentified={handleIdentified}
                />
              </>
            )}

            {step === '2a' && (
              <ExistingPatientStep
                clientId={clientId}
                phone={phone}
                onVerified={handleVerified}
                onBack={() => setStep(1)}
              />
            )}

            {step === '2b' && (
              <NewPatientStep
                clientId={clientId}
                phone={phone}
                agreementForms={practiceInfo?.agreement_forms ?? []}
                acceptedInsurances={practiceInfo?.accepted_insurances ?? []}
                onRegistered={handleRegistered}
                onBack={() => setStep(1)}
              />
            )}

            {step === '2c' && (
              <ApptTypeStep
                practiceInfo={practiceInfo}
                onSelected={handleApptTypeSelected}
                onBack={() => setStep(isExistingPatient ? '2a' : '2b')}
              />
            )}

            {step === 3 && (
              <SlotPickerStep
                clientId={clientId}
                patientToken={patientToken}
                practiceInfo={practiceInfo}
                patientName={patientName}
                apptType={apptType}
                onBooked={handleBooked}
                onSessionExpired={handleSessionExpired}
                onBack={() => setStep('2c')}
              />
            )}

            {step === 4 && (
              <ConfirmationStep
                practiceInfo={practiceInfo}
                apptType={bookedApptType}
                slot={selectedSlot}
                patientName={patientName}
                onReset={handleReset}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </Shell>
  );
}
